import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AlertBannerTypeEnum } from '@vueling-ng/vy-ds/src/lib/components/globals/alert-banner';
import { AlertBanner } from 'app/shared/alerts/alert-banner/models/alert-banner.model';
import { StateManagerService } from 'app/shared/state/state-manager.service';
import { environment } from 'environments/environment';
import { distinctUntilChanged, forkJoin, map, Observable, switchMap } from 'rxjs';
import { UmbracoAlertResponse, UmbracoContent } from './models/umbraco-response.model';
import {
  HttpTextRequestOptions,
  RequestImgLayoutDefault,
  UmbracoEndpoints,
  UmbracoQueryParams
} from './models/umbraco.model';

@Injectable({
  providedIn: 'root'
})
export class UmbracoService {
  private readonly _httpClient: HttpClient = inject(HttpClient);
  private readonly _stateManagerService: StateManagerService = inject(StateManagerService);
  private readonly _lang$: Observable<string> = this._stateManagerService.state$.pipe(
    distinctUntilChanged(),
    map((state) => state.language!)
  );
  private readonly _updatePromotionalBanner$: Observable<UmbracoContent> = this._lang$.pipe(
    distinctUntilChanged(),
    switchMap((lang) => {
      return forkJoin({ promotionalBanner: this.getPromotionalBanner(lang), alerts: this.getAlertsBanner(lang) }).pipe(
        map((umbracoReponse) => {
          return { imageLayout: umbracoReponse.promotionalBanner, alerts: umbracoReponse.alerts };
        })
      );
    })
  );

  public initialize(): Observable<UmbracoContent> {
    return this._updatePromotionalBanner$;
  }

  private getAlertsBanner(culture: string): Observable<AlertBanner[]> {
    const request = `${environment.umbraco.baseUrl}/${UmbracoEndpoints.Base}/${UmbracoEndpoints.Alert}?${UmbracoQueryParams.Culture}${culture}&${UmbracoQueryParams.Platform}&${UmbracoQueryParams.Fields}`;

    return this._httpClient
      .get<UmbracoAlertResponse>(request)
      .pipe(map((response: UmbracoAlertResponse) => this.parseAlerts(response)));
  }

  private getPromotionalBanner(culture: string): Observable<string> {
    const request = `${environment.umbraco.baseUrl}/${UmbracoEndpoints.Base}/${UmbracoEndpoints.Banner}?${UmbracoQueryParams.Culture}${culture}&${UmbracoQueryParams.ProductType}${RequestImgLayoutDefault.keyProduct}`;

    const httpOptions: HttpTextRequestOptions = { responseType: 'text' };
    return this._httpClient.get(request, httpOptions).pipe(map((response: string) => this.parseImageLayout(response)));
  }

  private parseImageLayout(response: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(response, 'text/html');
    const img = doc.querySelector('img');
    return img?.getAttribute('src') ?? '';
  }

  private parseAlerts(response: UmbracoAlertResponse): AlertBanner[] {
    return response.alerts.flatMap((alert) => {
      return {
        alertType: alert.type === 'alert--info' ? AlertBannerTypeEnum.INFO : AlertBannerTypeEnum.WARNING,
        message: alert.message
      };
    });
  }
}
